import { SupabaseService } from '#services/supabase/supabase.service';
import { Injectable } from '@angular/core';
import { AuthChangeEvent, AuthSession, Session, Subscription, User } from '@supabase/supabase-js';

import { AuthUserResponse, PublicUser, OAuthUserResponse } from '#models/db/user/user.model';
import { OAuthSignUpRequest, SignUpRequest } from '#models/db/user/sign-up-request.model';
import { OAuthSignInRequest, SignInRequest } from '#models/db/user/sign-in-request.model';
import { logger } from '#lib/logger';
import {
  CAPACITOR_DESIGNATED_CONTACTS_REDIRECT_TO,
  CAPACITOR_REDIRECT_TO,
  CAPACITOR_RESET_PASSWORD_REDIRECT_TO,
  designatedContactsConfig,
  oauthConfig,
  resetPasswordConfig,
} from '#services/auth/auth.config';
import { FriendlyErrorMessage } from '#models/error/error-message.model';
import { Capacitor } from '@capacitor/core';
import { ResponseBase } from '#models/db/response/response-base.model';

@Injectable({
  providedIn: 'root',
})
export class AuthRepositoryService {
  /**
   * Subscribes to supabase-js authentication state changes.
   *
   * **Note:**
   *
   * `Subscription` is a supabase-js interface that provides a way to unsubscribe from the event.
   * Make sure to call `unsubscribe()` when issuing component is destroyed to avoid memory leaks.
   *
   * @see {@link authChanges}
   */
  authChangeEvents: Subscription | null = null;

  private approvedProviders = ['google', 'facebook', 'apple'];
  private oauthConfig = oauthConfig;
  private resetPasswordConfig = resetPasswordConfig;
  private designatedContactsConfig = designatedContactsConfig;

  get designatedContactsRedirectTo(): string {
    return this.designatedContactsConfig.redirectTo!;
  }

  constructor(private supabaseService: SupabaseService) {
    this.configureOAuth();
  }

  /**
   * Configures the OAuth settings based on the platform.
   * If the platform is Capacitor, it updates the OAuth configuration
   * to include a specific redirect URL.
   *
   * @private
   */
  private configureOAuth() {
    if (Capacitor.isNativePlatform()) {
      this.oauthConfig = {
        ...this.oauthConfig,
        redirectTo: CAPACITOR_REDIRECT_TO,
      };

      this.resetPasswordConfig = {
        ...this.resetPasswordConfig,
        redirectTo: CAPACITOR_RESET_PASSWORD_REDIRECT_TO,
      };

      this.designatedContactsConfig = {
        ...this.designatedContactsConfig,
        redirectTo: CAPACITOR_DESIGNATED_CONTACTS_REDIRECT_TO,
      };
    }

    logger.log(this.oauthConfig, 'oauthConfig -> configureOAuth');
    logger.log(this.resetPasswordConfig, 'resetPasswordConfig -> configureOAuth');
    logger.log(this.designatedContactsConfig, 'designatedContactsConfig -> configureOAuth');
  }

  /**
   * Retrieves the currently authenticated user from the Supabase authentication service.
   *
   * @returns {Promise<User | null>} A promise that resolves to the authenticated user object if available, or `null`
   * if an error occurs or no user is authenticated.
   *
   * @remarks
   * Gets the current user details if there is an existing session. This method performs a network request to the Supabase Auth server,
   * so the returned value is authentic and can be used to base authorization rules on.
   */
  async getUser(): Promise<User | null> {
    const { data, error } = await this.supabaseService.supabase.auth.getUser();

    if (error) {
      logger.log(error, 'error -> AuthRepositoryService.getUser');
      return null;
    }

    return data.user;
  }

  /**
   * Retrieves the current authentication session.
   *
   * @returns {Promise<AuthSession | null>}
   * A promise that resolves to the current authentication session if available, or null if an error occurs or no session exists.
   *
   * @throws Will throw an error if the Supabase service returns an error while fetching the session.
   *
   * @remarks
   * * This method retrieves the current local session (i.e local storage).
   * * The session contains a signed JWT and unencoded session data.
   * * Since the unencoded session data is retrieved from the local storage medium, do not rely on it as a source of trusted data on the server.
   * It could be tampered with by the sender.
   * * If you need verified, trustworthy user data, call {@link getUser} instead.
   * If the session has an expired access token, this method will use the refresh token to get a new session.
   */
  async getSession(): Promise<AuthSession | null> {
    const { data, error } = await this.supabaseService.supabase.auth.getSession();

    if (error) {
      logger.log(error, 'error -> AuthRepositoryService.getSession');
      throw error;
    }

    return data.session;
  }

  /**
   * Refreshes the current session
   *
   * @throws Will throw an error if the Supabase authentication service returns an error.
   *
   * @private
   */
  private async refreshSession() {
    const { data, error } = await this.supabaseService.supabase.auth.refreshSession();

    if (error) {
      logger.log(error, 'error -> AuthRepositoryService.refreshSession');
      throw error;
    }

    const { session } = data;

    logger.log(
      { session },
      'response -> AuthRepositoryService.signUpWithEmail.supabase.auth.refreshSession'
    );
  }

  /**
   * Handles the OAuth redirect and sets the session using the provided redirect URL.
   *
   * @param {string} redirectTo - The URL to redirect to after OAuth authentication.
   * @returns {Promise<AuthSession | null>} - A promise that resolves to the authentication session or null if an error occurs.
   *
   * @throws {Error} - Throws an error if setting the session fails.
   */
  async setSessionOnOAuthRedirect(redirectTo: string): Promise<AuthSession | null> {
    const url = new URL(redirectTo);
    const params = new URLSearchParams(url.hash.slice(1));

    const { data, error } = await this.supabaseService.supabase.auth.setSession({
      access_token: params.get('access_token') ?? '',
      refresh_token: params.get('refresh_token') ?? '',
    });

    if (error) {
      logger.log(error, 'error -> AuthRepositoryService.setSessionOnOAuthRedirect');
      throw error;
    }

    logger.log(data.session, 'response -> AuthRepositoryService.setSessionOnOAuthRedirect');

    return data.session;
  }

  /**
   * Subscribes to authentication state changes and triggers the provided callback function.
   *
   * @param callback - A function to be called when the authentication state changes.
   * The function receives two arguments:
   * - `event`: An `AuthChangeEvent` indicating the type of authentication change.
   * - `session`: A `Session` object representing the current session, or `null` if there is no active session.
   */
  authChanges(callback: (event: AuthChangeEvent, session: Session | null) => void) {
    this.authChangeEvents =
      this.supabaseService.supabase.auth.onAuthStateChange(callback).data.subscription;
  }

  /**
   * Signs up a new user using the provided email and password.
   *
   * @param {SignUpRequest} body - The request body containing the email and password.
   *
   * @throws Throws any other errors encountered during the sign-up process.
   */
  async signUpWithEmail(body: SignUpRequest): Promise<PublicUser> {
    if (!body) {
      throw new Error('The request body is missing.');
    }

    const { email, password } = body;

    // Signs up a new user using the provided email and password.
    const { data: authData, error: authError } = await this.supabaseService.supabase.auth.signUp({
      email,
      password,
    });

    if (authError) {
      if (authError.code === 'user_already_exists' && authError.status === 422) {
        throw FriendlyErrorMessage.UnknownError;
      }

      logger.log(authError, 'error -> AuthRepositoryService.signUpWithEmail.supabase.auth.signUp');

      throw authError;
    }

    logger.log(
      { data: authData },
      'response -> AuthRepositoryService.signUpWithEmail.supabase.auth.signUp'
    );

    // Invokes the 'create-user' function in Supabase to create a new user.
    const { data: userData, error: userError } =
      await this.supabaseService.supabase.functions.invoke<ResponseBase<PublicUser>>(
        'create-user',
        {
          body: {
            provider: 'email',
            signUpData: {
              ...body,
              authId: authData?.user?.id,
            },
          },
        }
      );

    if (userError) {
      logger.log(
        userError,
        'error -> AuthRepositoryService.signUpWithEmail.supabase.functions.create-user'
      );

      throw userError;
    }

    logger.log(
      { data: userData!.details },
      'response -> AuthRepositoryService.signUpWithEmail.supabase.functions.create-user'
    );

    await this.refreshSession();

    return userData!.details;
  }

  /**
   * Signs in a user with the provided email and password.
   *
   * @param {SignInRequest} body - The request body containing the email and password.
   *
   * @throws If the request body is missing or if there is an error during sign-in.
   */
  async signIn(body: SignInRequest): Promise<AuthUserResponse> {
    if (!body) {
      throw new Error('The request body is missing.');
    }

    const { email, password } = body;

    const { data, error } = await this.supabaseService.supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      logger.log(error, 'error -> AuthRepositoryService.signIn');

      throw error;
    }

    logger.log(
      { data },
      'response -> AuthRepositoryService.signIn.supabase.auth.signInWithPassword'
    );

    return data;
  }

  /**
   * Signs in a user using OAuth.
   *
   * @param {OAuthSignInRequest} body - The request body containing the OAuth provider information.
   *
   * @throws {Error} If the provider is not supported or if there is an error during the sign-in process.
   */
  async signInWithOAuth(body: OAuthSignInRequest): Promise<OAuthUserResponse> {
    const { provider } = body;

    if (!this.approvedProviders.includes(provider)) {
      throw new Error('The provider is not supported.');
    }

    const { data, error } = await this.supabaseService.supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${this.oauthConfig.redirectTo}&provider=${provider}`,
      },
    });

    if (error) {
      logger.log(error, 'error -> AuthRepositoryService.signInWithOAuth');
      throw error;
    }

    logger.log(
      { data, redirectTo: this.oauthConfig.redirectTo },
      'response -> AuthRepositoryService.signInWithOAuth.supabase.auth.signInWithOAuth'
    );

    return data;
  }

  /**
   * Signs the user out from the application.
   *
   * @param global - If true, signs out the user globally across all devices.
   *
   * @remarks
   * Will not throw an error if the sign-out process fails.
   */
  async signOut(global = false): Promise<void> {
    const options = global ? { scope: 'global' as const } : { scope: 'local' as const };
    const { error } = await this.supabaseService.supabase.auth.signOut(options);

    if (error) {
      logger.log(error, 'error -> AuthRepositoryService.signOut');

      return;
    }

    return;
  }

  /**
   * Creates a new OAuth user in the system.
   *
   * This method retrieves the current authenticated user from Supabase, extracts the necessary user metadata,
   * and invokes a Supabase function to create a new user in the database. It also refreshes the session to
   * update the JWT claim with the new user's public ID.
   *
   * @param {OAuthSignUpRequest} body - The request body containing the OAuth sign-up information.
   *
   * @throws {Error} Throws an error if the request body is missing or if there is an error during the user creation process.
   */
  async createOAuthUser(body: OAuthSignUpRequest): Promise<void> {
    if (!body) {
      throw new Error('The request body is missing.');
    }

    const { data: userData, error: userError } = await this.supabaseService.supabase.auth.getUser();

    if (userError) {
      logger.log(userError, 'error -> AuthRepositoryService.createOAuthUser');

      throw userError;
    }

    logger.log(userData, 'response -> AuthRepositoryService.createOAuthUser.supabase.auth.getUser');

    const { firstName, lastName } = this.extractFirstAndLastName(
      userData.user.user_metadata['full_name'] || ''
    );

    const user_provider = body.provider;

    // Invokes the 'create-user' function in Supabase to create a new user.
    const { data: createdUserData, error: createdUserError } =
      await this.supabaseService.supabase.functions.invoke<PublicUser>('create-user', {
        body: {
          provider: user_provider,
          signUpData: {
            firstName,
            lastName,
            email: userData.user.user_metadata['email'],
            authId: userData.user.id,
            profileImageUrl: userData.user.user_metadata['avatar_url'] ?? '',
          },
        },
      });

    if (createdUserError) {
      logger.log(createdUserError, 'error -> AuthRepositoryService.createOAuthUser');

      throw createdUserError;
    }

    logger.log(
      { data: createdUserData },
      'response -> AuthRepositoryService.createOAuthUser.supabase.functions.create-user'
    );

    // Refresh session to get updated JWT claim with public.user_id
    await this.refreshSession();
  }

  /**
   * Sends a password reset email to the specified user.
   *
   * This method uses the Supabase authentication service to initiate a password reset
   * process for the provided email address.
   *
   * @param email - The email address of the user requesting a password reset.
   * @returns A promise that resolves when the password reset email has been sent.
   *
   * @remarks
   * The password reset email will contain a link that allows the user to set a new password.
   * If the email address is not associated with any user, the Supabase service will handle it gracefully
   * without revealing whether the email exists in the system.
   */
  async resetPassword(email: string): Promise<void> {
    const { error } = await this.supabaseService.supabase.auth.resetPasswordForEmail(email, {
      redirectTo: this.resetPasswordConfig.redirectTo,
    });

    if (error) {
      logger.log(error, 'error -> AuthRepositoryService.changePassword');
      throw error;
    }

    logger.log('', 'response -> AuthRepositoryService.changePassword.supabase.auth.updateUser');

    return;
  }

  /**
   * Changes the current user's password using the Supabase authentication service.
   *
   * @param password - The new password to set for the user.
   * @returns A promise that resolves when the password has been successfully changed.
   *
   * @remarks
   * Only use this method in conjunction with the `resetPassword` method.
   * The `resetPassword` method should be used to send a password reset email to the user,
   * and this method should be used to change the password after the user has clicked the link in the email.
   */
  async updatePassword(password: string): Promise<User> {
    const { data, error } = await this.supabaseService.supabase.auth.updateUser({
      password,
    });

    if (error) {
      logger.log(error, 'error -> AuthRepositoryService.changePassword');
      throw error;
    }

    logger.log(data, 'response -> AuthRepositoryService.changePassword.supabase.auth.updateUser');

    return data.user;
  }

  /**
   * Creates a new user from a designated contact invite using the provided sign-up request body.
   *
   * @param body - The sign-up request data containing user information and password.
   * @returns A promise that resolves to the newly created public user.
   *
   * @remarks
   * This method first updates the user's password using the `updatePassword` method,
   * then invokes the Supabase 'create-user' function to create a new user with the provided sign-up data.
   */
  async createUserFromDesignatedContactInvite(body: SignUpRequest): Promise<PublicUser> {
    const user: User = await this.updatePassword(body.password);

    // Invokes the 'create-user' function in Supabase to create a new user.
    const { data: userData, error: userError } =
      await this.supabaseService.supabase.functions.invoke<PublicUser>('create-user', {
        body: {
          provider: 'email',
          signUpData: {
            ...body,
            authId: user?.id,
          },
        },
      });

    if (userError) {
      logger.log(
        userError,
        'error -> AuthRepositoryService.signUpWithEmail.supabase.functions.create-user'
      );

      throw userError;
    }

    logger.log(
      { data: userData },
      'response -> AuthRepositoryService.signUpWithEmail.supabase.functions.create-user'
    );

    await this.refreshSession();

    return userData!;
  }

  /**
   * Extracts first and last names from a full name string.
   * - If only one word, assigns it to firstName, lastName is empty.
   * - If multiple words, firstName is all but last word, lastName is last word.
   * - Trims whitespace and handles edge cases.
   *
   * @param fullname The full name string
   * @returns An object with firstName and lastName
   */
  private extractFirstAndLastName(fullname: string): { firstName: string; lastName: string } {
    let firstName = '';
    let lastName = '';

    if (fullname && fullname.trim()) {
      const nameParts = fullname.trim().split(/\s+/);

      if (nameParts.length === 1) {
        firstName = nameParts[0];
      } else if (nameParts.length > 1) {
        firstName = nameParts.slice(0, -1).join(' ');
        lastName = nameParts[nameParts.length - 1];
      }
    }

    return { firstName, lastName };
  }
}
